import { ChangedField, ChangedFields } from "../ChangedFields";
import { PARAM } from "./SkaiciuokleParam";
import { DarbuotojoTarifasType, DraudejoGrupeType, DraudejoTipasType, NPDDuomenysType, SutartiesTipasType } from "./SkaiciuokleParamType";

export type TaikomasNpd = "standartinis" | "netaikomas" | "npd0_25" | "npd30_55"
export type Pensijai = "nekaupia" | "kaupiaPalaipsniui" | "kaupia"
export type DraudejoGrupe = "I" | "II" | "III" | "IV"
export type DraudejoTipas = "privatus" | "biudzetine" | "lietuvosBankas" | "politinesPartijos" | "profesinesSajungos" | "religinesBendrijos"
export type SutartiesTipas = "neterminuota" | "terminuota"

// default year will be current year if not provided
const defaultYear = () => new Date().getFullYear().toString();

// safe accessor for PARAM entries by year with sensible fallback
const getParamsForYear = (metai: string) => {
  if (PARAM[metai]) return PARAM[metai];
  const current = new Date().getFullYear().toString();
  if (PARAM[current]) return PARAM[current];
  const keys = Object.keys(PARAM);
  return keys.length > 0 ? (PARAM as any)[keys[0]] : (PARAM as any);
};

export type SkaiciuokleState = {
  iRankas: number;
  antPopieriaus: number;
  darboVietosKaina: number;
  apskaiciuotasNpd: number;
  darbuotojoImoka: number;
  gpm: number;
  darbdavioImoka: number;

  metai: string;
  taikomasNpd: TaikomasNpd;
  pensijai: Pensijai;
  draudejoGrupe: DraudejoGrupe;
  draudejoTipas: DraudejoTipas;
  sutartiesTipas: SutartiesTipas;

  changedField: ChangedField;
  darbuotojoTarifas: number;
  darbdavioTarifas: number;
  gpmTarifas: number;
}

export const format = (number: any) => {
  let num = +number;

  if (isNaN(number)) num = 0;

  return num.toFixed(2);
};

export const formatAsEmptyIfZero = (number: any) => {
  if (number === 0) return "";

  return number;
};

export const emptyState: (yearOverride?: string) => SkaiciuokleState = (
  yearOverride?: string
) => {
  const year = yearOverride ?? defaultYear();
  const state: SkaiciuokleState = {
    iRankas: 0,
    antPopieriaus: 0,
    darboVietosKaina: 0,
    apskaiciuotasNpd: 0,
    darbuotojoImoka: 0,
    gpm: 0,
    darbdavioImoka: 0,
    metai: year,
    taikomasNpd: "standartinis",
    pensijai: "nekaupia",
    draudejoGrupe: "I",
    draudejoTipas: "privatus",
    sutartiesTipas: "neterminuota",
    darbdavioTarifas: calculateDarbdavioTarifas(
      year,
      "neterminuota",
      "privatus",
      "I"
    ),
    darbuotojoTarifas: calculateDarbuotojoTarifas(year, "nekaupia"),
    gpmTarifas: calculateGpmTarifas(year),
    changedField: ChangedFields.GROSS,
  };

  return state;
};

export const changeIRankas = (newValue: any, state: SkaiciuokleState) => {
  if (isNaN(newValue)) {
    newValue = newValue.replace(",", ".");
  }

  if (!newValue || isNaN(newValue)) {
    return { ...emptyState(), iRankas: newValue };
  }

  let iRankas = +newValue;
  let antPopieriaus = calculateAntPopieriaus(
    state.metai,
    iRankas,
    state.taikomasNpd,
    state.pensijai
  );
  let apskaiciuotasNpd = calculateApskaiciuotasNpd(
    state.metai,
    antPopieriaus,
    state.taikomasNpd
  );
  let gpm = calculateGpm(antPopieriaus, apskaiciuotasNpd);
  let darbuotojoTarifas = calculateDarbuotojoTarifas(
    state.metai,
    state.pensijai
  );
  let darbuotojoImoka = calculateDarbuotojoImoka(
    antPopieriaus,
    darbuotojoTarifas
  );
  let darbdavioTarifas = calculateDarbdavioTarifas(
    state.metai,
    state.sutartiesTipas,
    state.draudejoTipas,
    state.draudejoGrupe
  );
  let darbdavioImoka = calculateDarbdavioImoka(antPopieriaus, darbdavioTarifas);
  let darboVietosKaina = calculateDarboVietosKaina(
    antPopieriaus,
    darbdavioImoka
  );

  const newState: SkaiciuokleState = {
    ...state,
    changedField: ChangedFields.NET,
    antPopieriaus,
    iRankas: newValue,
    apskaiciuotasNpd,
    darbuotojoImoka,
    darbuotojoTarifas,
    darbdavioImoka,
    darbdavioTarifas,
    darboVietosKaina,
    gpm
  };

  return newState;
};

export const changeAntPopieriaus = (newValue: any, state: SkaiciuokleState) => {
  if (isNaN(newValue)) {
    newValue = newValue.replace(",", ".");
  }

  if (!newValue || isNaN(newValue)) {
    // console.log({ ...emptyState(), antPopieriaus: newValue }, state)
    return { ...state, antPopieriaus: newValue };
  }

  let antPopieriaus = +newValue;
  let apskaiciuotasNpd = calculateApskaiciuotasNpd(
    state.metai,
    antPopieriaus,
    state.taikomasNpd
  );
  let gpm = calculateGpm(antPopieriaus, apskaiciuotasNpd);
  let darbuotojoTarifas = calculateDarbuotojoTarifas(
    state.metai,
    state.pensijai
  );
  let darbuotojoImoka = calculateDarbuotojoImoka(
    antPopieriaus,
    darbuotojoTarifas
  );
  let iRankas = calculateIRankas(antPopieriaus, gpm, darbuotojoImoka);
  let darbdavioTarifas = calculateDarbdavioTarifas(
    state.metai,
    state.sutartiesTipas,
    state.draudejoTipas,
    state.draudejoGrupe
  );
  let darbdavioImoka = calculateDarbdavioImoka(antPopieriaus, darbdavioTarifas);
  let darboVietosKaina = calculateDarboVietosKaina(
    antPopieriaus,
    darbdavioImoka
  );

  const newState: SkaiciuokleState = {
    ...state,
    changedField: ChangedFields.GROSS,
    antPopieriaus: newValue,
    iRankas,
    apskaiciuotasNpd,
    darbuotojoImoka,
    darbuotojoTarifas,
    darbdavioImoka,
    darbdavioTarifas,
    darboVietosKaina,
    gpm
  };

  return newState;
};

export const changeDarboVietosKaina = (newValue: any, state: SkaiciuokleState) => {
  if (isNaN(newValue)) {
    newValue = newValue.replace(",", ".");
  }

  if (!newValue || isNaN(newValue)) {
    return { ...emptyState(), darboVietosKaina: newValue };
  }

  let darboVietosKaina = +newValue;
  let darbdavioTarifas = calculateDarbdavioTarifas(
    state.metai,
    state.sutartiesTipas,
    state.draudejoTipas,
    state.draudejoGrupe
  );
  let antPopieriaus = calculateAntPopieriausIsDarboVietosKainos(
    darboVietosKaina,
    darbdavioTarifas
  );
  let apskaiciuotasNpd = calculateApskaiciuotasNpd(
    state.metai,
    antPopieriaus,
    state.taikomasNpd
  );
  let gpm = calculateGpm(antPopieriaus, apskaiciuotasNpd);
  let darbuotojoTarifas = calculateDarbuotojoTarifas(
    state.metai,
    state.pensijai
  );
  let darbuotojoImoka = calculateDarbuotojoImoka(
    antPopieriaus,
    darbuotojoTarifas
  );
  let iRankas = calculateIRankas(antPopieriaus, gpm, darbuotojoImoka);
  let darbdavioImoka = calculateDarbdavioImoka(antPopieriaus, darbdavioTarifas);

  const newState: SkaiciuokleState = {
    ...state,
    changedField: ChangedFields.EXPENSES,
    antPopieriaus,
    iRankas,
    apskaiciuotasNpd,
    darbuotojoImoka,
    darbuotojoTarifas,
    darbdavioImoka,
    darbdavioTarifas,
    darboVietosKaina: newValue,
    gpm
  };

  return newState;
};

const changedInput = (state: SkaiciuokleState) => {
  if (state.changedField === ChangedFields.GROSS)
    return changeAntPopieriaus(state.antPopieriaus, state);

  if (state.changedField === ChangedFields.NET)
    return changeIRankas(state.iRankas, state);

  if (state.changedField === ChangedFields.EXPENSES)
    return changeDarboVietosKaina(state.darboVietosKaina, state);

  return changeAntPopieriaus(state.antPopieriaus, state);
};

export const changeSutartiesTipas = (sutartiesTipas: SutartiesTipas, state: SkaiciuokleState) => {
  state.sutartiesTipas = sutartiesTipas;
  return changedInput(state);
};

export const changePensijai = (pensijai: Pensijai, state: SkaiciuokleState) => {
  state.pensijai = pensijai;
  return changedInput(state);
};

export const changeTaikomasNpd = (taikomasNpd: TaikomasNpd, state: SkaiciuokleState) => {
  state.taikomasNpd = taikomasNpd;
  return changedInput(state);
};

export const changeDraudejoTipas = (draudejoTipas: DraudejoTipas, state: SkaiciuokleState) => {
  state.draudejoTipas = draudejoTipas;
  return changedInput(state);
};

export const changeDraudejoGrupe = (draudejoGrupe: DraudejoGrupe, state: SkaiciuokleState) => {
  state.draudejoGrupe = draudejoGrupe;
  return changedInput(state);
};

export const changeMetai = (metai: string, state: SkaiciuokleState) => {
  state.metai = metai;
  return changedInput(state);
};

export const calculateAntPopieriausIsDarboVietosKainos = (
  darboVietosKaina: number,
  darbdavioTarifas: number
) => {
  return round((darboVietosKaina / (100 + darbdavioTarifas)) * 100);
};

export const calculateDarboVietosKaina = (
  antPopieriaus: number,
  darbdavioImoka: number
) => {
  let rounded = round(antPopieriaus + darbdavioImoka);
  return rounded;
};

export const calculateAntPopieriaus = (
  metai: string,
  iRankas: number,
  taikomasNpd: TaikomasNpd,
  pensijai: Pensijai
) => {
  const params = getParamsForYear(metai);
  const galimiNpdDuomenys = params.gpm.npdDuomenys.filter((o: NPDDuomenysType) => o.tipas === taikomasNpd);

  /** Kai galimos kelios formulės, tai pirma apskaičiuojama suma ant popieriaus, tada tikrinama, 
   * ar galėjo būti taikoma
   */
  for (let i = 0; i < galimiNpdDuomenys.length; i++) {
    const npdDuomenys = galimiNpdDuomenys[i]
    const antPopieriaus = calculateAntPopieriausTaikantNpd(metai, iRankas, pensijai, npdDuomenys)
    if (antPopieriaus <= npdDuomenys.taikomaIki)
      return antPopieriaus
  }

  return calculateAntPopieriausNetaikantNpd(metai, iRankas, pensijai);
};

export const calculateAntPopieriausTaikantNpd = (metai: string,
  iRankas: number,
  pensijai: Pensijai,
  npdDuomenys?: NPDDuomenysType
) => {
  let socDrTarifas = calculateDarbuotojoTarifas(metai, pensijai);
  let fixNpd = npdDuomenys?.fixNpd ?? 0;
  let maxPajamosLygiosNpd = (fixNpd * (100 - socDrTarifas)) / 100;
  if (iRankas <= maxPajamosLygiosNpd)
    return calculateAntPopieriausNpdLygusPajamoms(metai, iRankas, pensijai);

  let mma = npdDuomenys?.mma ?? 0;
  let gpmTarifas = getParamsForYear(metai).gpm.tarifas;
  let maxPajamosFiksuotasNpd =
    mma === 0
      ? Number.MAX_VALUE
      : mma * (1 - socDrTarifas / 100) - ((mma - fixNpd) * gpmTarifas) / 100;
  if (iRankas <= maxPajamosFiksuotasNpd)
    return calculateAntPopieriausNpdPastovus(metai, iRankas, pensijai, fixNpd);

  let maxPajamos = npdDuomenys?.maxPajamos || 0;
  let maxPajamosMazejantisNpd =
    maxPajamos === 0
      ? Number.MAX_VALUE
      : maxPajamos * (1 - socDrTarifas / 100 - gpmTarifas / 100);
  if (iRankas <= maxPajamosMazejantisNpd)
    return calculateAntPopieriausNpdMazejantis(metai, iRankas, pensijai, npdDuomenys);

  return calculateAntPopieriausNetaikantNpd(metai, iRankas, pensijai);
}

export const calculateAntPopieriausNpdLygusPajamoms = (
  metai: string,
  iRankas: number,
  pensijai: Pensijai
) => {
  let darbuotojoTarifas = calculateDarbuotojoTarifas(metai, pensijai);
  let antPopieriaus = round((iRankas / (100 - darbuotojoTarifas)) * 100);
  return antPopieriaus;
};

export const calculateAntPopieriausNpdPastovus = (
  metai: string,
  iRankas: number,
  pensijai: Pensijai,
  npd: number
) => {
  let darbuotojoTarifas = calculateDarbuotojoTarifas(metai, pensijai);
  let gpmTarifas = getParamsForYear(metai).gpm.tarifas;
  
  // fallback for gpmTarifas if PARAM missing
  // (kept here for older callers that might not have params variable)
  // Note: prefer getParamsForYear(metai) above where available
  let antPopieriaus = round(
    ((iRankas - (npd * gpmTarifas) / 100) /
      (100 - gpmTarifas - darbuotojoTarifas)) *
    100
  );
  return antPopieriaus;
};

export const calculateAntPopieriausNpdMazejantis = (
  metai: string,
  iRankas: number,
  pensijai: Pensijai,
  npdDuomenys?: NPDDuomenysType
) => {
  let darbuotojoTarifas = calculateDarbuotojoTarifas(metai, pensijai);
  let mma = npdDuomenys?.mma ?? 0;
  let npdParam1 = npdDuomenys?.fixNpd ?? 0;
  let npdParam2 = npdDuomenys?.npdBeta ?? 0;
  let gpmTarifas = getParamsForYear(metai).gpm.tarifas;
  let antPopieriaus = round(
    ((iRankas -
      (npdParam1 * gpmTarifas) / 100 -
      (npdParam2 * mma * gpmTarifas) / 100) /
      (100 - gpmTarifas - npdParam2 * gpmTarifas - darbuotojoTarifas)) *
    100
  );
  return antPopieriaus;
};

export const calculateAntPopieriausNetaikantNpd = (
  metai: string,
  iRankas: number,
  pensijai: Pensijai
) => {
  let darbuotojoTarifas = calculateDarbuotojoTarifas(metai, pensijai);
  let gpmTarifas = getParamsForYear(metai).gpm.tarifas;
  let antPopieriaus = round(
    (iRankas / (100 - gpmTarifas - darbuotojoTarifas)) * 100
  );
  return antPopieriaus;
};

export const calculateGpmTarifas = (metai: string) => {
  return getParamsForYear(metai).gpm.tarifas;
};

export const calculateDarbuotojoTarifas = (metai: string, pensijai: Pensijai) => {
  return getParamsForYear(metai).darbuotojoTarifas[pensijai as keyof DarbuotojoTarifasType];
};

export const calculateDarbdavioTarifas = (
  metai: string,
  sutartiesTipas: SutartiesTipas,
  draudejoTipas: DraudejoTipas,
  draudejoGrupe: DraudejoGrupe
) => {
  const params = getParamsForYear(metai);
  let nedarbo = params.sutartiesTipas[sutartiesTipas as keyof SutartiesTipasType];
  let garantiniamFonduiIrIlgNed = params.draudejoTipas[draudejoTipas as keyof DraudejoTipasType];
  let nelaiminguAtsitikimu = params.draudejoGrupe[draudejoGrupe as keyof DraudejoGrupeType];

  return round(nedarbo + garantiniamFonduiIrIlgNed + nelaiminguAtsitikimu);
};

export const calculateDarbdavioImoka = (
  antPopieriaus: number,
  tarifas: number
) => {
  return round((antPopieriaus * tarifas) / 100);
};

export const round = (number: number) => {
  let rounded = Math.round((number + Number.EPSILON) * 100) / 100;
  return rounded;
};

export const calculateIRankas = (
  antPopieriaus: number,
  gpm: number,
  darbuotojoImoka: number
) => {
  return round(antPopieriaus - gpm - darbuotojoImoka);
};

export const calculateDarbuotojoImoka = (
  antPopieriaus: number,
  tarifas: number
) => {
  let imoka = round((antPopieriaus * tarifas) / 100);
  return imoka;
};

export const calculateGpm = (
  antPopieriaus: number,
  apskaiciuotasNpd: number
) => {
  let imoka = round(((antPopieriaus - apskaiciuotasNpd) * 20) / 100);
  return imoka;
};

export const calculateApskaiciuotasNpd = (
  metai: string,
  antPopieriaus: number,
  taikomasNpd: TaikomasNpd
) => {
  if (antPopieriaus < 0) return 0;

  const npdDuomenys = getParamsForYear(metai).gpm.npdDuomenys.find((o: NPDDuomenysType) => o.tipas === taikomasNpd && o.taikomaIki >= antPopieriaus);
  let maxPajamosLygiosNpd = npdDuomenys?.fixNpd || 0;
  if (antPopieriaus <= maxPajamosLygiosNpd) return antPopieriaus;

  let maxPajamosFiksuotasNpd = npdDuomenys?.mma || Number.MAX_VALUE;
  if (antPopieriaus <= maxPajamosFiksuotasNpd) return maxPajamosLygiosNpd;

  let maxPajamosTaikomasNpd = npdDuomenys?.maxPajamos || 0;

  if (antPopieriaus <= maxPajamosTaikomasNpd) {
    let mma = npdDuomenys?.mma ?? 0;
    let npdBeta = npdDuomenys?.npdBeta ?? 0;
    return round(maxPajamosLygiosNpd - npdBeta * (antPopieriaus - mma));
  }

  return 0;
};
